import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Handles scroll-to-section for hash links (e.g. /#about, /#retreat).
 *
 * How it works:
 * 1. On any location change that has a hash, wait briefly for the page to
 *    render (especially important when navigating from another route to /),
 *    then find the target element and scroll to it.
 * 2. The 120ms delay lets React render the home page content before we try
 *    to find the section element.
 */
export default function HashScroll() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) return;

    const id = hash.replace("#", "");

    // Try immediately first (works when already on the right page)
    const scrollToEl = () => {
      const el = document.getElementById(id);
      if (el) {
        // Account for the fixed navbar height (60px)
        const navH = 60;
        const top  = el.getBoundingClientRect().top + window.scrollY - navH - 16;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        return true;
      }
      return false;
    };

    if (!scrollToEl()) {
      // Element not in DOM yet (navigating from another route) — wait for render
      const t1 = setTimeout(() => {
        if (!scrollToEl()) {
          // Second attempt with longer delay for slower renders
          const t2 = setTimeout(scrollToEl, 300);
          return () => clearTimeout(t2);
        }
      }, 120);
      return () => clearTimeout(t1);
    }
  }, [pathname, hash]);

  return null;
}
